//////////////////////////////////////////////////////////////////////////////
//
//  캠페인 발송 이력 다이얼로그
//
//  작성자: MediInStep Team
//  생성일: 2025-12-29
//
//  설명: 캠페인별 푸시 발송 이력 조회 다이얼로그
//
//////////////////////////////////////////////////////////////////////////////

import { Component } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Pagination from "@mui/material/Pagination";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { request } from "app/network";
import { formatLocalDateTime } from "utils/dateFormatter";

interface CampaignHistoryDialogProps {
    open: boolean;
    campaign: any | null;
    onClose: () => void;
}

interface CampaignHistoryDialogState {
    history: any[];
    loading: boolean;
    pageNumber: number;
    pageSize: number;
    totalCount: number;
}

class CampaignHistoryDialogClass extends Component<CampaignHistoryDialogProps, CampaignHistoryDialogState> {
    constructor(props: CampaignHistoryDialogProps) {
        super(props);

        this.state = {
            history: [],
            loading: false,
            pageNumber: 1,
            pageSize: 20,
            totalCount: 0,
        };
    }

    componentDidUpdate(prevProps: CampaignHistoryDialogProps) {
        if (this.props.open && !prevProps.open && this.props.campaign) {
            this.loadHistory();
        }
    }

    loadHistory = async () => {
        if (!this.props.campaign) return;

        try {
            this.setState({ loading: true });

            const res = await request("PushCampaign.getHistory", {
                pathParams: { id: this.props.campaign.seqId.toString() },
                query: {
                    pageNumber: this.state.pageNumber,
                    pageSize: this.state.pageSize,
                },
            });

            console.log("Campaign History Response:", res);

            if (res && res.data) {
                const pagedList = res.data;
                this.setState({
                    history: pagedList.items || [],
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("발송 이력 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        this.setState({ pageNumber: value }, () => this.loadHistory());
    };

    getStatusChip = (history: any) => {
        if (history.readDt) {
            return <Chip label="읽음" color="success" size="small" />;
        } else if (history.deliveredDt) {
            return <Chip label="전달됨" color="info" size="small" />;
        } else if (history.sentDt) {
            return <Chip label="발송됨" color="primary" size="small" />;
        } else if (history.errorCode) {
            return <Chip label="실패" color="error" size="small" />;
        } else {
            return <Chip label="대기" color="default" size="small" />;
        }
    };

    render() {
        const { open, campaign, onClose } = this.props;
        const { history, loading, pageNumber, pageSize, totalCount } = this.state;
        const totalPages = Math.ceil(totalCount / pageSize);

        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <MDTypography variant="h4" fontWeight="medium">
                        발송 이력 - {campaign?.name}
                    </MDTypography>
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                            <CircularProgress />
                        </MDBox>
                    ) : history.length === 0 ? (
                        <MDBox p={3} textAlign="center">
                            <MDTypography variant="h6" color="text">
                                발송 이력이 없습니다
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>사용자</TableCell>
                                            <TableCell>제목</TableCell>
                                            <TableCell>상태</TableCell>
                                            <TableCell>발송 시간</TableCell>
                                            <TableCell>전달 시간</TableCell>
                                            <TableCell>읽음 시간</TableCell>
                                            <TableCell>오류</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {history.map((item) => (
                                            <TableRow key={item.seqId}>
                                                <TableCell>{item.userName || `User #${item.userSeqId}`}</TableCell>
                                                <TableCell>{item.title}</TableCell>
                                                <TableCell>{this.getStatusChip(item)}</TableCell>
                                                <TableCell>
                                                    {item.sentDt ? formatLocalDateTime(item.sentDt) : "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {item.deliveredDt ? formatLocalDateTime(item.deliveredDt) : "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {item.readDt ? formatLocalDateTime(item.readDt) : "-"}
                                                </TableCell>
                                                <TableCell>
                                                    {item.errorCode ? (
                                                        <MDTypography variant="caption" color="error">
                                                            {item.errorCode}: {item.errorMessage}
                                                        </MDTypography>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {totalPages > 1 && (
                                <MDBox mt={3} display="flex" justifyContent="center">
                                    <Pagination
                                        count={totalPages}
                                        page={pageNumber}
                                        onChange={this.handlePageChange}
                                        color="primary"
                                    />
                                </MDBox>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={onClose} color="secondary">
                        닫기
                    </MDButton>
                </DialogActions>
            </Dialog>
        );
    }
}

function CampaignHistoryDialog(props: CampaignHistoryDialogProps) {
    return <CampaignHistoryDialogClass {...props} />;
}

export default CampaignHistoryDialog;
